import React from 'react';
import { Box, Typography, Button, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { displayName } from '../../../../helpers/displayName';

export interface GroupLeader {
    id: string;
    fullname?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
}

interface GroupLeadersCardProps {
    leaders: GroupLeader[];
    onAddLeader: () => void;
    onRemoveLeader: (leaderId: string) => void;
}

export const GroupLeadersCard: React.FC<GroupLeadersCardProps> = ({
    leaders,
    onAddLeader,
    onRemoveLeader,
}) => {
    const getDisplayName = (l: GroupLeader) =>
        // No `|| l.id` tail: resolveLeader returns a bare { id } whenever the
        // system-user lookup misses (including a swallowed failure), which put a
        // raw ObjectId on screen where a person's name belongs.
        displayName(l.fullname || [l.firstName, l.lastName].filter(Boolean).join(' ') || l.email);
    const getInitials = (l: GroupLeader) => {
        const first = l.firstName || l.fullname?.split(' ')[0] || '?';
        const last = l.lastName || l.fullname?.split(' ').slice(1).join(' ') || '?';
        return `${first[0]}${last[0]}`.toUpperCase();
    };

    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '14px' }}>
                <SupervisorAccountIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Group Leaders</Typography>
                <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted, ml: 0.5 }}>
                    ({leaders.length})
                </Typography>
            </Box>

            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mb: '12px' }}>
                Group Leaders receive alert notifications and manage teams within this group. Leaders must be System Users.
            </Typography>

            {leaders.length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, fontStyle: 'italic', mb: '12px' }}>
                    No leaders assigned yet.
                </Typography>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '12px' }}>
                    <thead>
                        <tr>
                            <th style={{
                                textAlign: 'left', padding: '6px 10px',
                                fontSize: '11px', fontWeight: 600, color: GT_COLORS.textFaint,
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                borderBottom: `1px solid ${GT_COLORS.border}`,
                            }}>Name</th>
                            <th style={{
                                width: 40, padding: '6px 10px',
                                borderBottom: `1px solid ${GT_COLORS.border}`,
                            }} />
                        </tr>
                    </thead>
                    <tbody>
                        {leaders.map((leader) => (
                            <tr key={leader.id}>
                                <td style={{
                                    padding: '8px 10px',
                                    borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <Box sx={{
                                            width: 32, height: 32, borderRadius: '50%',
                                            backgroundColor: GT_COLORS.primary, color: '#fff',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '12px', fontWeight: 600, flexShrink: 0,
                                        }}>
                                            {getInitials(leader)}
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                                                {getDisplayName(leader)}
                                            </Typography>
                                            <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint }}>
                                                {leader.email || '—'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </td>
                                <td style={{
                                    padding: '8px 10px',
                                    borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                                }}>
                                    <IconButton
                                        size="small"
                                        onClick={() => onRemoveLeader(leader.id)}
                                        sx={{
                                            color: GT_COLORS.textFaint,
                                            '&:hover': { color: GT_COLORS.red, bgcolor: `${GT_COLORS.red}10` },
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <Button
                fullWidth
                variant="outlined"
                startIcon={<PersonAddIcon />}
                onClick={onAddLeader}
                sx={{
                    mt: 'auto',
                    textTransform: 'none',
                    fontSize: '14px',
                    borderRadius: '8px',
                    ...(leaders.length === 0
                        ? {
                            '@keyframes addGroupLeaderPulse': {
                                '0%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}88` },
                                '70%': { boxShadow: `0 0 0 18px ${GT_COLORS.primary}00` },
                                '100%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}00` },
                            },
                            backgroundColor: GT_COLORS.primary,
                            color: '#fff',
                            border: `1px solid ${GT_COLORS.primary}`,
                            animation: 'addGroupLeaderPulse 2s ease-in-out infinite',
                            fontWeight: 600,
                            '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                        }
                        : {
                            color: GT_COLORS.textMuted,
                            borderColor: GT_COLORS.borderInput,
                        }
                    ),
                }}
            >
                Add Leader
            </Button>
        </Box>
    );
};
